import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { RegisterForm, GoogleLoginButton, AuthDivider, AuthHeader } from '../../components/auth';
import { validators } from '../../utils/formValidation';

const RegisterAdmin = () => {
  const navigate = useNavigate();
  const { register, error, success, registerMutation } = useAdminAuth();

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    password_confirm: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (success) {
  toast.success("Inscription réussie ! Veuillez consulter l'e-mail qui vous a été envoyé pour activer votre compte.", {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        password: '',
        password_confirm: '',
      });
      setFormErrors({});
      setTouched({});

      const timer = setTimeout(() => {
        navigate('/');
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  }, [error]);

  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'email':
        error = validators.email(value);
        break;
      case 'first_name':
        error = validators.name(value, 'Prénom');
        break;
      case 'last_name':
        error = validators.name(value, 'Nom');
        break;
      case 'password':
        error = validators.password(value);
        break;
      case 'password_confirm':
        error = validators.passwordConfirm(value, formData.password);
        break;
      default:
        break;
    }

    setFormErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    return error;
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleFieldBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  const handleSubmit = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      setTouched(Object.keys(formData).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      return;
    }

    register({
      email: formData.email,
      first_name: formData.first_name,
      last_name: formData.last_name,
      password: formData.password,
      password_confirm: formData.password_confirm,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-teal-50 flex items-center justify-center px-4 py-6 overflow-y-auto scrollbar-hide">
      <div className="w-full max-w-3xl py-4">
        <div className="bg-white rounded-3xl shadow-2xl p-10 md:p-12">
          <AuthHeader title="Inscription" subtitle="Rejoignez Easy Survey et gérez vos enquêtes" />

          <RegisterForm
            formData={formData}
            formErrors={formErrors}
            touched={touched}
            isLoading={registerMutation.isPending}
            onFieldChange={handleFieldChange}
            onFieldBlur={handleFieldBlur}
            onSubmit={handleSubmit}
          />

          <AuthDivider text="Ou" />

          <div className="mb-8">
            <GoogleLoginButton />
          </div>

          <AuthDivider text="Vous avez déjà un compte ?" />

          <Link
            to="/"
            className="block w-full py-2 rounded-xl bg-gray-100 text-gray-900 font-semibold text-center flex items-center justify-center hover:bg-gray-200 transition-colors duration-200 border-2 border-gray-200"
          >
            Se connecter
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterAdmin;
